(() => {
  'use strict';

  if (window.__kickassPromotionWidgetsLoaded) return;
  window.__kickassPromotionWidgetsLoaded = true;

  const PROMO_CONTAINER_SELECTOR = '[data-promo-container], [data-announcement-bar]';

  const dispatchLayoutUpdate = () => {
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
      document.dispatchEvent(new CustomEvent('announcement-bar:updated'));
      document.dispatchEvent(new CustomEvent('promotion-widget:updated'));
    });
  };

  const logDesignModeWarning = (message, error) => {
    if (!window.Shopify || !window.Shopify.designMode) return;

    if (error) {
      console.warn(message, error);
    } else {
      console.warn(message);
    }
  };

  const getPromoContainer = (element) => element.closest(PROMO_CONTAINER_SELECTOR);

  const containerRequiresCountdown = (container) => {
    if (!container) return false;

    if (container.hasAttribute('data-require-countdown')) {
      return container.dataset.requireCountdown === 'true';
    }

    return Boolean(container.querySelector('announcement-countdown'));
  };

  const containerRequiresUrlQualification = (container) => {
    if (!container) return false;
    return container.dataset.requireUrlQualification === 'true';
  };

  const updatePromoContainerVisibility = (container) => {
    if (!container) return;

    const requiresCountdown = containerRequiresCountdown(container);
    const requiresUrlQualification = containerRequiresUrlQualification(container);
    const countdownReady = !requiresCountdown || container.dataset.countdownReady === 'true';
    const urlQualified = !requiresUrlQualification || container.dataset.urlQualified === 'true';
    const shouldShow = countdownReady && urlQualified;
    const wasHidden = container.hidden;

    container.hidden = !shouldShow;

    if (shouldShow) {
      container.removeAttribute('aria-hidden');
    } else {
      container.setAttribute('aria-hidden', 'true');
    }

    const layoutWrapper = container.closest('[data-promo-layout-wrapper]');
    if (layoutWrapper && layoutWrapper !== container) {
      layoutWrapper.hidden = !shouldShow;
    }

    if (wasHidden !== container.hidden) {
      dispatchLayoutUpdate();
    }
  };

  if (!customElements.get('announcement-countdown')) {
    class AnnouncementCountdown extends HTMLElement {
      connectedCallback() {
        this.stopTimer();

        this.promoContainer = getPromoContainer(this);
        this.daysElement = this.querySelector('[data-countdown-days]');
        this.hoursElement = this.querySelector('[data-countdown-hours]');
        this.minutesElement = this.querySelector('[data-countdown-minutes]');
        this.secondsElement = this.querySelector('[data-countdown-seconds]');
        this.compactCountdown = this.dataset.compactCountdown === 'true';

        this.selectedDate = {
          year: Number.parseInt(this.dataset.countdownYear, 10),
          month: Number.parseInt(this.dataset.countdownMonth, 10),
          day: Number.parseInt(this.dataset.countdownDay, 10),
          hour: Number.parseInt(this.dataset.countdownHour, 10),
          minute: Number.parseInt(this.dataset.countdownMinute, 10),
          second: 0
        };

        this.timeZone = this.dataset.countdownTimezone || 'Australia/Brisbane';
        this.endTime = this.getCountdownTimestamp();

        if (!Number.isFinite(this.endTime)) {
          logDesignModeWarning('Promotion hidden because the countdown date or timezone is invalid.');
          this.setCountdownReady(false);
          return;
        }

        if (this.endTime <= Date.now()) {
          this.setCountdownReady(false);
          return;
        }

        this.setCountdownReady(true);
        this.updateCountdown();
        this.scheduleNextUpdate();
      }

      disconnectedCallback() {
        this.stopTimer();
      }

      scheduleNextUpdate() {
        this.stopTimer();
        const delay = 1000 - (Date.now() % 1000) + 10;

        this.timer = window.setTimeout(() => {
          this.updateCountdown();

          if (this.endTime > Date.now()) {
            this.scheduleNextUpdate();
          }
        }, delay);
      }

      stopTimer() {
        if (this.timer) {
          window.clearTimeout(this.timer);
          this.timer = null;
        }
      }

      setCountdownReady(isReady) {
        if (!this.promoContainer) return;
        this.promoContainer.dataset.countdownReady = isReady ? 'true' : 'false';
        updatePromoContainerVisibility(this.promoContainer);
      }

      getCountdownTimestamp() {
        const { year, month, day, hour, minute, second } = this.selectedDate;

        if (
          !Number.isInteger(year) ||
          !Number.isInteger(month) ||
          !Number.isInteger(day) ||
          !Number.isInteger(hour) ||
          !Number.isInteger(minute) ||
          year < 2000 ||
          month < 1 ||
          month > 12 ||
          day < 1 ||
          day > 31 ||
          hour < 0 ||
          hour > 23 ||
          minute < 0 ||
          minute > 59
        ) {
          return Number.NaN;
        }

        try {
          const timestamp = this.zonedDateTimeToUtc(
            year,
            month,
            day,
            hour,
            minute,
            second,
            this.timeZone
          );

          if (!this.matchesSelectedDate(timestamp)) {
            const selected = this.selectedDate;

            logDesignModeWarning(
              [
                'Promotion countdown hidden because the selected local time',
                `${selected.year}-${String(selected.month).padStart(2, '0')}-${String(selected.day).padStart(2, '0')}`,
                `${String(selected.hour).padStart(2, '0')}:${String(selected.minute).padStart(2, '0')}`,
                `does not exist or is ambiguous in ${this.timeZone}.`,
                'Choose another hour, particularly when the date falls on a daylight-saving transition.'
              ].join(' ')
            );

            return Number.NaN;
          }

          return timestamp;
        } catch (error) {
          logDesignModeWarning(
            'Promotion countdown could not parse the selected date and timezone.',
            error
          );
          return Number.NaN;
        }
      }

      zonedDateTimeToUtc(year, month, day, hour, minute, second, timeZone) {
        const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
        const initialOffset = this.getTimeZoneOffset(utcGuess, timeZone);
        let timestamp = utcGuess - initialOffset;
        const correctedOffset = this.getTimeZoneOffset(timestamp, timeZone);

        if (correctedOffset !== initialOffset) {
          timestamp = utcGuess - correctedOffset;
        }

        return timestamp;
      }

      getTimeZoneOffset(timestamp, timeZone) {
        const parts = this.getZonedParts(timestamp, timeZone);
        const representedAsUtc = Date.UTC(
          parts.year,
          parts.month - 1,
          parts.day,
          parts.hour,
          parts.minute,
          parts.second
        );

        return representedAsUtc - timestamp;
      }

      getZonedParts(timestamp, timeZone) {
        const formatter = new Intl.DateTimeFormat('en-AU', {
          timeZone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hourCycle: 'h23'
        });

        return formatter.formatToParts(new Date(timestamp)).reduce((parts, part) => {
          if (part.type !== 'literal') {
            parts[part.type] = Number.parseInt(part.value, 10);
          }
          return parts;
        }, {});
      }

      matchesSelectedDate(timestamp) {
        const actual = this.getZonedParts(timestamp, this.timeZone);
        const selected = this.selectedDate;

        return (
          actual.year === selected.year &&
          actual.month === selected.month &&
          actual.day === selected.day &&
          actual.hour === selected.hour &&
          actual.minute === selected.minute
        );
      }

      updateCountdown() {
        const remaining = this.endTime - Date.now();

        if (remaining <= 0) {
          this.stopTimer();
          this.setCountdownReady(false);
          return;
        }

        const totalSeconds = Math.max(0, Math.floor(remaining / 1000));
        const days = Math.floor(totalSeconds / 86400);
        const hours = this.compactCountdown
          ? Math.floor(totalSeconds / 3600)
          : Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        if (this.daysElement) this.daysElement.textContent = String(days).padStart(2, '0');
        if (this.hoursElement) this.hoursElement.textContent = String(hours).padStart(2, '0');
        if (this.minutesElement) this.minutesElement.textContent = String(minutes).padStart(2, '0');
        if (this.secondsElement) this.secondsElement.textContent = String(seconds).padStart(2, '0');

        this.setAttribute(
          'aria-label',
          `${days} days, ${hours} hours, ${minutes} minutes and ${seconds} seconds remaining`
        );
      }
    }

    customElements.define('announcement-countdown', AnnouncementCountdown);
  }

  if (!customElements.get('announcement-copy-code')) {
    class AnnouncementCopyCode extends HTMLElement {
      connectedCallback() {
        this.disconnectButton();
        this.stopResetTimer();

        this.promoContainer = getPromoContainer(this);
        this.button = this.querySelector('button');
        this.label = this.querySelector('[data-copy-label]');
        this.status = this.querySelector('[data-copy-status]');
        this.code = (this.dataset.discountCode || '').trim();
        this.defaultText = this.dataset.defaultText || 'Copy';
        this.copiedText = this.dataset.copiedText || 'Copied!';
        this.failedText = this.dataset.failedText || 'Copy failed';

        const isUrlGated = this.dataset.urlGated === 'true';
        const hideContainerWhenUnqualified = this.dataset.hideContainerWhenUnqualified === 'true';
        const isQualified = !isUrlGated || this.isUrlQualified();

        if (this.promoContainer) {
          this.promoContainer.dataset.urlQualified = isQualified ? 'true' : 'false';

          if (hideContainerWhenUnqualified) {
            this.promoContainer.dataset.requireUrlQualification = 'true';
          }

          updatePromoContainerVisibility(this.promoContainer);
        }

        if (!isQualified) {
          this.hidden = true;
          return;
        }

        this.hidden = false;

        if (!this.button || !this.code) return;

        this.handleClick = this.copyCode.bind(this);
        this.button.addEventListener('click', this.handleClick);
      }

      disconnectedCallback() {
        this.disconnectButton();
        this.stopResetTimer();
      }

      disconnectButton() {
        if (this.button && this.handleClick) {
          this.button.removeEventListener('click', this.handleClick);
        }
        this.handleClick = null;
      }

      stopResetTimer() {
        if (this.resetTimer) {
          window.clearTimeout(this.resetTimer);
          this.resetTimer = null;
        }
      }

      isUrlQualified() {
        const previewInEditor = this.dataset.previewInThemeEditor === 'true';

        if (previewInEditor && window.Shopify && window.Shopify.designMode) {
          return true;
        }

        const parameterName = (this.dataset.urlParameter || '')
          .trim()
          .replace(/^[?&]+/, '')
          .split('=')[0]
          .trim();

        const requiredValue = (this.dataset.urlParameterValue || '').trim();
        const rememberForSession = this.dataset.rememberSession === 'true';
        const instanceId = this.dataset.instanceId || this.dataset.sectionId || 'default';

        if (!parameterName) return false;

        const storageKey = [
          'promotion-discount-qualified',
          instanceId,
          parameterName,
          requiredValue
        ].join(':');

        const parameters = new URLSearchParams(window.location.search);
        let qualified = parameters.has(parameterName);

        if (qualified && requiredValue !== '') {
          qualified = parameters.getAll(parameterName).includes(requiredValue);
        }

        if (!rememberForSession) return qualified;

        try {
          if (qualified) {
            window.sessionStorage.setItem(storageKey, 'true');
          } else {
            qualified = window.sessionStorage.getItem(storageKey) === 'true';
          }
        } catch (error) {
          // Continue without persistence if session storage is blocked.
        }

        return qualified;
      }

      async copyCode() {
        try {
          if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(this.code);
          } else {
            this.copyUsingFallback();
          }

          this.showCopiedState();
        } catch (error) {
          try {
            this.copyUsingFallback();
            this.showCopiedState();
          } catch (fallbackError) {
            this.showCopyErrorState();
          }
        }
      }

      copyUsingFallback() {
        const textarea = document.createElement('textarea');
        textarea.value = this.code;
        textarea.setAttribute('readonly', '');
        textarea.setAttribute('aria-hidden', 'true');
        textarea.style.position = 'fixed';
        textarea.style.top = '0';
        textarea.style.left = '-9999px';
        textarea.style.opacity = '0';

        document.body.appendChild(textarea);

        try {
          textarea.focus({ preventScroll: true });
        } catch (error) {
          textarea.focus();
        }

        textarea.select();
        textarea.setSelectionRange(0, textarea.value.length);

        const copied = document.execCommand('copy');
        textarea.remove();

        if (!copied) {
          throw new Error('Copy command failed');
        }
      }

      showCopiedState() {
        this.stopResetTimer();

        if (this.label) this.label.textContent = this.copiedText;
        if (this.status) this.status.textContent = `${this.code} copied to clipboard`;

        if (this.button) {
          this.button.classList.remove('is-error');
          this.button.classList.add('is-copied');
        }

        this.scheduleButtonReset();
      }

      showCopyErrorState() {
        this.stopResetTimer();

        if (this.label) this.label.textContent = this.failedText;
        if (this.status) {
          this.status.textContent = `Unable to copy. Discount code: ${this.code}`;
        }

        if (this.button) {
          this.button.classList.remove('is-copied');
          this.button.classList.add('is-error');
        }

        this.scheduleButtonReset();
      }

      scheduleButtonReset() {
        this.resetTimer = window.setTimeout(() => {
          if (this.label) this.label.textContent = this.defaultText;
          if (this.status) this.status.textContent = '';

          if (this.button) {
            this.button.classList.remove('is-copied', 'is-error');
          }

          this.resetTimer = null;
        }, 2200);
      }
    }

    customElements.define('announcement-copy-code', AnnouncementCopyCode);
  }

  if (!customElements.get('ka-promo-price')) {
    class KaPromoPrice extends HTMLElement {
      connectedCallback() {
        this.disconnectListeners();

        this.priceElement = this.querySelector('[data-promo-discounted-price]');
        this.formId = (this.dataset.productFormId || '').trim();
        this.discountType = this.dataset.discountType || 'percentage';
        this.discountValue = Number.parseFloat(this.dataset.discountValue || '0');
        this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
        this.currencyCode = (this.dataset.currencyCode || '').trim().toUpperCase();
        this.pricePrefix = this.dataset.pricePrefix || '';
        this.priceSuffix = this.dataset.priceSuffix || '';
        this.currentVariantId = null;

        this.variants = new Map();

        const variantDataElement = this.querySelector('[data-promo-price-variants]');

        if (variantDataElement) {
          try {
            const variants = JSON.parse(variantDataElement.textContent);

            variants.forEach((variant) => {
              if (variant && variant.id != null) {
                this.variants.set(String(variant.id), {
                  basePrice: Number(variant.basePrice)
                });
              }
            });
          } catch (error) {
            logDesignModeWarning(
              'Promo code banner could not parse its variant price data.',
              error
            );
          }
        }

        this.productScope =
          this.closest('.product') ||
          this.closest('[data-handle]') ||
          document;

        this.handleProductChange = (event) => {
          const target = event.target;

          if (
            !target ||
            !(
              target.matches?.('[name="id"]') ||
              target.matches?.('[name^="options["]') ||
              target.closest?.('variant-selects')
            )
          ) {
            return;
          }

          this.scheduleRefresh();
        };

        this.handleVariantEvent = (event) => {
          const eventVariant =
            event?.detail?.variant ||
            event?.detail?.productVariant ||
            event?.detail?.selectedVariant;

          if (eventVariant?.id && this.variants.has(String(eventVariant.id))) {
            this.renderVariant(String(eventVariant.id));
            return;
          }

          this.scheduleRefresh();
        };

        this.productScope.addEventListener(
          'change',
          this.handleProductChange
        );

        [
          'variant:change',
          'variant-change',
          'product:variant-change',
          'product:variant-changed'
        ].forEach((eventName) => {
          document.addEventListener(eventName, this.handleVariantEvent);
          window.addEventListener(eventName, this.handleVariantEvent);
        });

        this.refreshFromProductForm();
      }

      disconnectedCallback() {
        this.disconnectListeners();
      }

      disconnectListeners() {
        if (this.refreshFrame) {
          window.cancelAnimationFrame(this.refreshFrame);
          this.refreshFrame = null;
        }

        if (this.refreshTimer) {
          window.clearTimeout(this.refreshTimer);
          this.refreshTimer = null;
        }

        if (this.productScope && this.handleProductChange) {
          this.productScope.removeEventListener(
            'change',
            this.handleProductChange
          );
        }

        if (this.handleVariantEvent) {
          [
            'variant:change',
            'variant-change',
            'product:variant-change',
            'product:variant-changed'
          ].forEach((eventName) => {
            document.removeEventListener(eventName, this.handleVariantEvent);
            window.removeEventListener(eventName, this.handleVariantEvent);
          });
        }

        this.productScope = null;
        this.handleProductChange = null;
        this.handleVariantEvent = null;
      }

      scheduleRefresh() {
        if (this.refreshFrame) {
          window.cancelAnimationFrame(this.refreshFrame);
        }

        if (this.refreshTimer) {
          window.clearTimeout(this.refreshTimer);
        }

        this.refreshFrame = window.requestAnimationFrame(() => {
          this.refreshFrame = null;
          this.refreshFromProductForm();

          // Some themes update the hidden variant ID asynchronously after
          // the option change event. This is a one-off event-driven retry,
          // not continuous polling.
          this.refreshTimer = window.setTimeout(() => {
            this.refreshFromProductForm();
            this.refreshTimer = null;
          }, 80);
        });
      }

      refreshFromProductForm() {
        const selectedVariantId = this.getSelectedVariantId();

        if (selectedVariantId && this.variants.has(selectedVariantId)) {
          this.renderVariant(selectedVariantId);
        }
      }

      getSelectedVariantId() {
        if (this.formId) {
          const form = document.getElementById(this.formId);

          if (form) {
            const formVariantField = form.querySelector('[name="id"]');

            if (formVariantField?.value) {
              return String(formVariantField.value);
            }
          }

          const escapedFormId = this.escapeAttributeValue(this.formId);
          const externalVariantField = document.querySelector(
            `[name="id"][form="${escapedFormId}"]`
          );

          if (externalVariantField?.value) {
            return String(externalVariantField.value);
          }
        }

        const selectedVariantId = this.dataset.selectedVariantId;

        return selectedVariantId ? String(selectedVariantId) : null;
      }

      escapeAttributeValue(value) {
        return String(value)
          .replace(/\\/g, '\\\\')
          .replace(/"/g, '\\"');
      }

      renderVariant(variantId) {
        if (!this.priceElement || !this.variants.has(variantId)) return;
        if (this.currentVariantId === variantId) return;

        const variant = this.variants.get(variantId);
        const basePrice = Number(variant.basePrice);

        if (!Number.isFinite(basePrice)) return;

        const discountedPrice = this.calculateDiscountedPrice(basePrice);

        this.priceElement.textContent = this.formatMoney(
          discountedPrice,
          this.moneyFormat
        );

        this.currentVariantId = variantId;
        this.dataset.selectedVariantId = variantId;

        const accessibleText = [
          this.pricePrefix,
          this.priceElement.textContent,
          this.priceSuffix
        ].filter(Boolean).join(' ');

        if (accessibleText) {
          this.setAttribute('aria-label', accessibleText);
        }
      }

      calculateDiscountedPrice(basePrice) {
        const configuredDiscount = Number.isFinite(this.discountValue)
          ? Math.max(0, this.discountValue)
          : 0;

        const discountValue = this.discountType === 'fixed_amount'
          ? configuredDiscount
          : Math.min(100, configuredDiscount);

        let discountAmount = 0;

        if (this.discountType === 'fixed_amount') {
          discountAmount = Math.round(discountValue * 100);
        } else {
          discountAmount = Math.round(basePrice * discountValue / 100);
        }

        return Math.max(0, Math.round(basePrice - discountAmount));
      }

      formatMoney(cents, format) {
        if (
          window.Shopify &&
          typeof window.Shopify.formatMoney === 'function'
        ) {
          try {
            return window.Shopify.formatMoney(cents, format);
          } catch (error) {
            // Use the local formatter below.
          }
        }

        const moneyFormat = format || '{{amount}}';
        const placeholderMatch = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);

        if (!placeholderMatch) {
          if (this.currencyCode) {
            try {
              return new Intl.NumberFormat(
                document.documentElement.lang || 'en-AU',
                {
                  style: 'currency',
                  currency: this.currencyCode
                }
              ).format(cents / 100);
            } catch (error) {
              // Fall through to a neutral numeric value.
            }
          }

          return this.formatWithDelimiters(cents / 100, 2, '.', ',');
        }

        let value;

        switch (placeholderMatch[1]) {
          case 'amount_no_decimals':
            value = this.formatWithDelimiters(cents / 100, 0, '.', ',');
            break;
          case 'amount_with_comma_separator':
            value = this.formatWithDelimiters(cents / 100, 2, ',', '.');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = this.formatWithDelimiters(cents / 100, 0, ',', '.');
            break;
          case 'amount_with_apostrophe_separator':
            value = this.formatWithDelimiters(cents / 100, 2, '.', "'");
            break;
          case 'amount':
          default:
            value = this.formatWithDelimiters(cents / 100, 2, '.', ',');
            break;
        }

        return moneyFormat.replace(placeholderMatch[0], value);
      }

      formatWithDelimiters(number, precision, decimal, thousands) {
        const numericValue = Number(number);

        if (!Number.isFinite(numericValue)) return '0';

        const fixed = numericValue.toFixed(precision);
        const parts = fixed.split('.');
        const dollars = parts[0].replace(
          /\B(?=(\d{3})+(?!\d))/g,
          thousands
        );

        return parts.length > 1
          ? `${dollars}${decimal}${parts[1]}`
          : dollars;
      }
    }

    customElements.define('ka-promo-price', KaPromoPrice);
  }

})();
